import { useCallback, useEffect } from 'react';

import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel.ts';
import {
	useCommentsPanelActions,
	useCommentsPanelState,
	ViewValues,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { useCommentService } from '@confluence/content-types-bridge-comments';
import {
	handleCommentsPanelNavigationWithViewChange,
	ScrollBehavior,
	NavigationContext,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils.ts';
import { fg } from '@confluence/feature-gating';
import { useUnreadCommentsActions } from '@confluence/unread-comments/entry-points/useUnreadComments.ts';

type UseRegisterCommentsPanelCallbacksOptions = {
	/**
	 * When the content type calls `confluenceAPI.comments.syncActiveCommentMarkers`, this
	 * callback receives the annotation IDs of the inline comments the content currently
	 * contains. When omitted, these calls are ignored.
	 */
	onSyncActiveCommentMarkers?: (markerRefs: string[]) => void;
};

export const useRegisterCommentsPanelCallbacks = ({
	onSyncActiveCommentMarkers,
}: UseRegisterCommentsPanelCallbacksOptions = {}): void => {
	const { commentService } = useCommentService();
	const { isCommentsPanelShown, showCommentsPanel, hideCommentsPanel } = useShowCommentsPanel();
	const { setCurrentlySelectedThreadKey, setCurrentView } = useCommentsPanelActions();
	const { currentView } = useCommentsPanelState();
	const { updateUnreadCommentIDSet } = useUnreadCommentsActions();

	const setCommentThreadHandler = useCallback(
		(threadId?: string) => {
			// highlight a comment in sidebar
			// if threadId is undefined, it means we are de-selecting
			setCurrentlySelectedThreadKey(threadId);
			if (threadId) {
				// Use unified navigation - content-types-bridge-comments only has inline comments
				let expectedView: ViewValues | undefined = undefined;
				if (currentView === ViewValues.RESOLVED) {
					expectedView = ViewValues.OPEN;
					setCurrentView && setCurrentView(ViewValues.OPEN);
				}
				void handleCommentsPanelNavigationWithViewChange({
					scrollBehavior: ScrollBehavior.SMOOTH,
					context: NavigationContext.PANEL,
					block: 'center',
					annotationId: threadId,
					commentType: 'INLINE',
					annotationClicked: true,
					expectedView,
				});
			}
		},
		[setCurrentlySelectedThreadKey, currentView, setCurrentView],
	);

	const triggerReadStatusCommentHandler = useCallback(
		(commentIds: string[], read: boolean = false) => {
			if (fg('cc-whiteboards-consolidating-comments-unread')) {
				if (read) {
					updateUnreadCommentIDSet(new Set(commentIds), 'remove');
				}
			}
		},
		[updateUnreadCommentIDSet],
	);

	const showPanelHandler = useCallback(
		(threadId?: string) => {
			if (!isCommentsPanelShown) {
				showCommentsPanel();
			}
			if (threadId) {
				setCommentThreadHandler(threadId);
			}

			// triggerCommentPanelRefresh handles the case where the view hasn't mounted yet
			// by setting a pending flag that fires once the handler is registered.
			void commentService.triggerCommentPanelRefresh();
		},
		[isCommentsPanelShown, showCommentsPanel, setCommentThreadHandler, commentService],
	);

	const hidePanelHandler = useCallback(() => {
		if (isCommentsPanelShown) {
			void hideCommentsPanel();
		}
	}, [isCommentsPanelShown, hideCommentsPanel]);

	useEffect(() => {
		commentService.registerSelectCommentThreadHandler(setCommentThreadHandler);
		commentService.registerShowPanelHandler(showPanelHandler);
		commentService.registerHidePanelHandler(hidePanelHandler);
		commentService.registerTriggerReadStatusComment(triggerReadStatusCommentHandler);
		return () => {
			// un-set the handlers on component unmount
			commentService.registerSelectCommentThreadHandler(undefined);
			commentService.registerShowPanelHandler(undefined);
			commentService.registerHidePanelHandler(undefined);
			commentService.registerTriggerReadStatusComment(undefined);
		};
	}, [
		commentService,
		showPanelHandler,
		hidePanelHandler,
		setCommentThreadHandler,
		triggerReadStatusCommentHandler,
	]);

	useEffect(() => {
		if (!onSyncActiveCommentMarkers) {
			return;
		}
		commentService.registerSyncActiveCommentMarkers(onSyncActiveCommentMarkers);
		return () => {
			commentService.registerSyncActiveCommentMarkers(undefined);
		};
	}, [commentService, onSyncActiveCommentMarkers]);
};
